import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

const StaffAugmentation = ({
  servicesLinks,
  setActive,
  linkClicked,
  setOpen,
}) => {
  // Combine all service links into a single array
  const allStaffAugLinks = [
    ...(servicesLinks?.links?.subLink1 || []),
    ...(servicesLinks?.links?.subLink2 || []),
  ];

  // Find the first index that is a category to use as default for desktop
  const firstCategoryIndex = allStaffAugLinks.findIndex(item => item.isCategory);
  const [hoveredIndex, setHoveredIndex] = useState(firstCategoryIndex !== -1 ? firstCategoryIndex : 0);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleDesktopClick = () => {
    setActive && setActive("");
    linkClicked && linkClicked();
  };

  const handleMobileClick = () => {
    setOpen && setOpen(false);
    linkClicked && linkClicked();
  };

  const toggleCategory = (index) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  // If no links, render nothing
  if (!allStaffAugLinks || allStaffAugLinks.length === 0) return null;

  return (
    <>
      {/* Desktop layout: Side-by-Side Mega Menu */}
      <div className="xl:block hidden z-10">
        <div className="bg-white flex rounded-xl border border-[#e5e5e5] shadow-2xl min-w-[680px] overflow-hidden">
          {/* Left Column: Categories Selection */}
          <div className="w-[220px] bg-[#F8F9FB] border-r border-[#eee] p-2 flex flex-col gap-1">
            {allStaffAugLinks.map((item, index) => (
              <li key={index} className="list-none">
                {item.isCategory ? (
                  <div
                    className={`flex items-center justify-between w-full px-4 py-3 text-base font-medium rounded-md cursor-pointer transition-all duration-200 ease-in-out ${
                      hoveredIndex === index
                        ? "bg-[#ffebee] text-[#d32f2f]"
                        : "text-[#4a4949] hover:bg-[#ffebee] hover:text-[#d32f2f]"
                    }`}
                    onMouseEnter={() => setHoveredIndex(index)}
                  >
                    <span>{item.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2.5"
                      stroke="currentColor"
                      className={`w-4 h-4 transition-transform duration-200 ${hoveredIndex === index ? 'translate-x-1' : 'opacity-40'}`}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                ) : (
                  <Link
                    to={item.link}
                    className={`block w-full px-4 py-3 text-base font-medium rounded-md transition-all duration-200 ${
                      hoveredIndex === index 
                        ? "bg-[#ffebee] text-[#d32f2f]" 
                        : "text-[#4a4949] hover:bg-[#ffebee] hover:text-[#d32f2f]"
                    }`}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onClick={handleDesktopClick}
                    target={item.target || "_self"}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </div>

          {/* Right Column: Active Category Content */}
          <div className="flex-1 p-5 bg-white">
            {allStaffAugLinks[hoveredIndex]?.isCategory ? (
              <div key={hoveredIndex} className="animate__animated animate__fadeIn animate__faster">
                <h3 className="text-[11px] font-bold text-[#9ca3af] uppercase tracking-[0.15em] mb-6 px-4 border-l-2 border-[#d32f2f] leading-none">
                  {allStaffAugLinks[hoveredIndex].name}
                </h3>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {allStaffAugLinks[hoveredIndex].children.map((child, childIndex) => (
                    <li key={childIndex} className="list-none">
                      <Link
                        to={child.link}
                        className="block w-full px-4 py-2.5 text-[15px] font-medium rounded-md transition-all duration-200 ease-in-out text-[#4a4949] hover:bg-[#ffebee] hover:text-[#d32f2f] hover:translate-x-1"
                        onClick={handleDesktopClick}
                        target={child.target || "_self"}
                      >
                        {child.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-16 h-16 bg-[#F8F9FB] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#d32f2f]/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-[#9ca3af] font-medium">Select a category to explore services</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile layout: Accordion style (unchanged for UX consistency) */}
      <div className="xl:hidden block pl-4">
        {allStaffAugLinks.map((item, index) => (
          <div key={index} className="border-b border-[#f3f4f6] last:border-b-0">
            {item.isCategory ? (
              <div>
                <div
                  className="flex items-center justify-between px-4 py-3 text-base text-[#4a4949] cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#ffebee] hover:text-[#d32f2f]"
                  onClick={() => toggleCategory(index)}
                >
                  <span>{item.name}</span>
                  {expandedCategory === index ? (
                    <IoIosArrowUp className="text-sm mr-2" />
                  ) : (
                    <IoIosArrowDown className="text-sm mr-2" />
                  )}
                </div>
                {/* Children sub-items */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedCategory === index ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="pl-4 border-l-2 border-[#E4002B]/20 ml-4 mb-2">
                    {item.children.map((child, childIndex) => (
                      <Link
                        key={childIndex}
                        to={child.link}
                        className="block px-4 py-2 text-sm text-[#4a4949] hover:text-[#d32f2f]"
                        onClick={handleMobileClick}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                to={item.link}
                className="block px-4 py-3 text-base text-[#4a4949] transition-all duration-200 ease-in-out hover:bg-[#ffebee] hover:text-[#d32f2f]"
                onClick={handleMobileClick}
              >
                {item.name}
              </Link>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default StaffAugmentation;
